/**
 * Investec brand style configuration — colors, typography, and prompt
 * rules extracted from the client's slide template (colorScheme
 * "Investec UK new" + fontScheme "Investec UK", layout placeholder
 * geometry, via the OOXML).
 *
 * The master's first layout is "Cover". Unqualified body inserts use
 * "Title and content" (first catalog match, slideLayout5). The OOXML
 * layout name has a trailing space; matching trims it. "Blank" and
 * "Title Only" have no content box.
 */

import type { ThemeColors } from "../../types";

export const INVESTEC_COLORS = {
  black: "000000", // dk1
  offWhite: "FAFBFA", // lt1 / lt2
  navy: "2E384A", // dk2 / accent1
  dustyBlue: "A5BCC5", // accent2 / folHlink
  warmGrey: "DBDAD3", // accent3
  teal: "00878A", // accent4
  burgundy: "7E3D4C", // accent5
  gold: "D5903C", // accent6 / hlink

  up: "00B050",
  down: "C00000",
} as const;

export const INVESTEC_THEME_COLORS: ThemeColors = {
  dark1: INVESTEC_COLORS.black,
  dark2: INVESTEC_COLORS.navy,
  light1: INVESTEC_COLORS.offWhite,
  light2: INVESTEC_COLORS.offWhite,
  accent1: INVESTEC_COLORS.navy,
  accent2: INVESTEC_COLORS.dustyBlue,
  accent3: INVESTEC_COLORS.warmGrey,
  accent4: INVESTEC_COLORS.teal,
  accent5: INVESTEC_COLORS.burgundy,
  accent6: INVESTEC_COLORS.gold,
  hyperlink: INVESTEC_COLORS.gold,
  followedHyperlink: INVESTEC_COLORS.dustyBlue,
  majorFont: "Times New Roman",
  minorFont: "Arial",
};

export const INVESTEC_CHART_SERIES_PALETTE = [
  INVESTEC_COLORS.navy,
  INVESTEC_COLORS.dustyBlue,
  INVESTEC_COLORS.teal,
  INVESTEC_COLORS.burgundy,
  INVESTEC_COLORS.gold,
  INVESTEC_COLORS.warmGrey,
];

export const INVESTEC_SLIDE_DIMENSIONS = { width: 842, height: 595.375 } as const;

export const INVESTEC_POSITIONS = {
  title: { left: 41.1, top: 45.4, width: 759.8, height: 28.3 },
  subtitle: { left: 41.1, top: 76.6, width: 759.8, height: 36.8 },
  content: { left: 41.1, top: 119.1, width: 759.8, height: 425.2 },
  footer: { left: 54.6, top: 575.5, width: 746.3, height: 14.2 },
  pageNumber: { left: 24.1, top: 558.5, width: 17.0, height: 17.0 },
} as const;

export const INVESTEC_STYLE_PROMPT = `

## INVESTEC BRAND STYLE OVERRIDE (active — apply these rules to ALL output)

REFERENCE PRECEDENTS: The attached "Investec.pptx" is the Investec visual standard. Match the closest relevant precedent for covers, title-and-content pages, and multi-column pages. Prefer its navy-led palette, Times New Roman titles, and Arial body over inventing a generic layout.

CRITICAL FONT RULE: You MUST include \`fontFamily\` on EVERY shape you create or modify — even when editing existing text. This OVERRIDES the general rule about matching the existing presentation's fonts. Investec uses:
- Titles / headings: \`"Times New Roman"\`
- Body / bullets / tables / charts / notes / subtitles / footers: \`"Arial"\`
NEVER omit fontFamily when Investec style is active. If a shape already has the wrong font (including Calibri), include fontFamily to correct it.

### Color palette (from the Investec master colorScheme "Investec UK new")
Use ONLY these Investec brand colors. Do NOT use arbitrary hex values.

**Theme mapping:**
- Text1 / dark1: Black #${INVESTEC_COLORS.black}
- Text2 / dark2: Navy #${INVESTEC_COLORS.navy}
- Background1 / light1: Off-white #${INVESTEC_COLORS.offWhite}
- Background2 / light2: Off-white #${INVESTEC_COLORS.offWhite}
- Accent1: Navy #${INVESTEC_COLORS.navy}
- Accent2: Dusty blue #${INVESTEC_COLORS.dustyBlue}
- Accent3: Warm grey #${INVESTEC_COLORS.warmGrey}
- Accent4: Teal #${INVESTEC_COLORS.teal}
- Accent5: Burgundy #${INVESTEC_COLORS.burgundy}
- Accent6: Gold #${INVESTEC_COLORS.gold}

**Usage:**
- **Primary brand / emphasis**: Navy #${INVESTEC_COLORS.navy}
- **Secondary emphasis**: Teal #${INVESTEC_COLORS.teal} or dusty blue #${INVESTEC_COLORS.dustyBlue}
- **Highlight**: Gold #${INVESTEC_COLORS.gold} or burgundy #${INVESTEC_COLORS.burgundy} — highlights, not default body colour
- **Text**: Navy #${INVESTEC_COLORS.navy} or black #${INVESTEC_COLORS.black}
- **Background / rules**: Off-white #${INVESTEC_COLORS.offWhite} or warm grey #${INVESTEC_COLORS.warmGrey}
- **Up / down**: Green #${INVESTEC_COLORS.up} / red #${INVESTEC_COLORS.down}
- **Chart series palette** (in order): ${INVESTEC_CHART_SERIES_PALETTE.map((c) => `#${c}`).join(", ")}
  Lead with navy (#${INVESTEC_COLORS.navy}) as the first/primary series color.

### Typography (authoritative — from "Title and content")
Slide dimensions: 842 × 595.375 pt (A4 landscape).

The OOXML layout name is "Title and content " (trailing space). Use that layout for ordinary body slides. The first catalog match is the geometry below.

**Placeholder positions:**
- **Title**: left=${INVESTEC_POSITIONS.title.left}, top=${INVESTEC_POSITIONS.title.top}, width=${INVESTEC_POSITIONS.title.width}, height=${INVESTEC_POSITIONS.title.height} — Times New Roman, navy.
- **Subtitle** (body idx 12): left=${INVESTEC_POSITIONS.subtitle.left}, top=${INVESTEC_POSITIONS.subtitle.top}, width=${INVESTEC_POSITIONS.subtitle.width}, height=${INVESTEC_POSITIONS.subtitle.height} — Arial, about 16pt.
- **Content** (idx 13): left=${INVESTEC_POSITIONS.content.left}, top=${INVESTEC_POSITIONS.content.top}, width=${INVESTEC_POSITIONS.content.width}, height=${INVESTEC_POSITIONS.content.height} — Arial.
- **Footer** (body idx 15): left=${INVESTEC_POSITIONS.footer.left}, top=${INVESTEC_POSITIONS.footer.top}, width=${INVESTEC_POSITIONS.footer.width}, height=${INVESTEC_POSITIONS.footer.height} — Arial, about 7pt. Master furniture includes "Confidential | Investec"; do not invent a second confidentiality line.
- **Slide number**: left=${INVESTEC_POSITIONS.pageNumber.left}, top=${INVESTEC_POSITIONS.pageNumber.top}.

**Layout choice:**
- Default body: "Title and content".
- Do not default to "Cover" (the master's first layout), "Blank" (footer and slide number only), or "Title Only" / "Title only" (no content box).
- Two-column, three-column, two-row, and header variants are specialist. Clone them only for the same job. Do not rotate through the rest of the layout catalog.

### Charts (Investec style)
- Font: Arial, minimum 8pt
- **Primary data color**: navy (#${INVESTEC_COLORS.navy}); multi-series uses the series palette above in order
- Data labels: positioned outside bars/columns (inside for stacked)
- Gridlines: minimal — none by default; only when no data labels are shown
- Legend: bottom-center placement (only when needed for multi-series)
- No 3D effects, gradients, or graphic fills

### Tables (Investec style)
- Header row: navy (#${INVESTEC_COLORS.navy}) fill with off-white text, bold
- Body: off-white fill, navy or black Arial text
- Alternate-row shading: warm grey (#${INVESTEC_COLORS.warmGrey})
- Borders: thin warm grey (#${INVESTEC_COLORS.warmGrey}); align numeric columns consistently

### Shapes & layout (Investec style)
- Separate content with sharp rectangular blocks — not rounded shapes
- Keep the footer and slide number on the baseline of the content layout
- Prefer "Title and content" for ordinary body slides

**TEXT FITTING RULE**: Keep labels and item descriptions SHORT (max 6-8 words each). The content box is wide; long sentences still overflow.
`;
